import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { ScrapeJobSourceFields } from '@/features/scraper/ScrapeJobSourceFields';
import {
  buildScrapeJobConfig,
  initialScrapeJobFormState,
  isScrapeJobFormValid,
  type ScraperCapabilities,
} from '@/features/scraper/scrapeJobForm';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '@/pages/ProjectsPage.module.css';

type Funnel = {
  jobs_count: number;
  discovered: number;
  pending: number;
  approved: number;
  rejected: number;
  with_email: number;
  high_confidence: number;
  with_contact_name: number;
  in_audience_leads: number;
};

type ScrapeJob = {
  id: string;
  name: string;
  status: string;
  config?: { source?: string; apify_preset?: string; apify_last_imported?: number };
  pending_count?: number;
  last_error?: string | null;
};

type ScrapeCandidate = {
  id: string;
  company: string | null;
  email: string | null;
  confidence_score: string;
  review_status: string;
};

export function AudienceScraperPanel({
  audienceId,
  audienceName,
}: {
  audienceId: string;
  audienceName: string;
}) {
  const qc = useQueryClient();
  const [jobForm, setJobForm] = useState(() => initialScrapeJobFormState());
  const [runNow, setRunNow] = useState(true);

  const scrapeCapsQ = useQuery({
    queryKey: ['scraper-capabilities'],
    queryFn: () =>
      apiFetch<ScraperCapabilities>('/api/scraper/capabilities'),
  });

  const catalogQ = useQuery({
    queryKey: ['scraper-acquisition-catalog'],
    queryFn: () =>
      apiFetch<{ entries: import('@marketing-hub/shared').AcquisitionCatalogEntry[] }>(
        '/api/scraper/acquisition-catalog',
      ),
  });

  const funnelQ = useQuery({
    queryKey: ['audience-scraper-funnel', audienceId],
    queryFn: () =>
      apiFetch<{ funnel: Funnel }>(`/api/audiences/${audienceId}/scraper-funnel`),
  });

  const jobsQ = useQuery({
    queryKey: ['audience-scrape-jobs', audienceId],
    queryFn: () =>
      apiFetch<{ jobs: ScrapeJob[] }>(`/api/audiences/${audienceId}/scrape-jobs`),
  });

  const candidatesQ = useQuery({
    queryKey: ['audience-scrape-candidates', audienceId],
    queryFn: () =>
      apiFetch<{ candidates: ScrapeCandidate[] }>(
        `/api/audiences/${audienceId}/scrape-candidates?status=pending`,
      ),
  });

  const createJob = useMutation({
    mutationFn: () => {
      const config = buildScrapeJobConfig(jobForm);
      return apiFetch(`/api/audiences/${audienceId}/scrape-jobs`, {
        method: 'POST',
        body: JSON.stringify({
          config,
          respect_robots: jobForm.jobSource === 'urls',
          run_after_create: runNow,
        }),
      });
    },
    onSuccess: () => {
      setJobForm(initialScrapeJobFormState());
      void qc.invalidateQueries({ queryKey: ['audience-scraper-funnel', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-scrape-jobs', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-scrape-candidates', audienceId] });
    },
  });

  const runJob = useMutation({
    mutationFn: (jobId: string) =>
      apiFetch(`/api/audiences/${audienceId}/scrape-jobs/${jobId}/run`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['audience-scrape-jobs', audienceId] }),
  });

  const syncLeads = useMutation({
    mutationFn: () =>
      apiFetch(`/api/audiences/${audienceId}/sync-scraper-leads`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['audience', audienceId] });
      void qc.invalidateQueries({ queryKey: ['audience-scraper-funnel', audienceId] });
    },
  });

  const f = funnelQ.data?.funnel;

  return (
    <section className={styles.card}>
      <h2>Scraper</h2>
      <p className={styles.mutedSmall}>
        Seed-URL of Apify (Maps/Search) voor <strong>{audienceName}</strong>. Goedgekeurde kandidaten
        komen in deze doelgroep. Review in{' '}
        <Link to="/app/scraper">Acquisitie → Scraper</Link>.
      </p>

      {funnelQ.isLoading ? <LoadingState /> : null}
      {f ? (
        <ul className={styles.list}>
          <li className={styles.mutedSmall}>
            <strong>{f.discovered}</strong> kandidaten gevonden ({f.jobs_count} jobs)
          </li>
          <li className={styles.mutedSmall}>
            <strong>{f.with_email}</strong> met e-mail · <strong>{f.high_confidence}</strong>{' '}
            high confidence (≥70) · <strong>{f.with_contact_name}</strong> met contactnaam
          </li>
          <li className={styles.mutedSmall}>
            Review: {f.pending} pending · {f.approved} approved · {f.rejected} rejected
          </li>
          <li className={styles.mutedSmall}>
            <strong>{f.in_audience_leads}</strong> contacten in doelgroep (incl. handmatig)
          </li>
        </ul>
      ) : null}

      <form
        className={styles.formGrid}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          createJob.mutate();
        }}
      >
        <ScrapeJobSourceFields
          form={jobForm}
          onChange={(patch) => setJobForm((prev) => ({ ...prev, ...patch }))}
          scrapeCaps={scrapeCapsQ.data}
          catalogEntries={catalogQ.data?.entries}
        />
        <label className={styles.mutedSmall}>
          <input type="checkbox" checked={runNow} onChange={(e) => setRunNow(e.target.checked)} />{' '}
          Direct runnen na aanmaken
        </label>
        <Button
          type="submit"
          disabled={createJob.isPending || !isScrapeJobFormValid(jobForm)}
        >
          Scrape job voor doelgroep
        </Button>
      </form>

      <h3>Jobs</h3>
      {jobsQ.isLoading ? <LoadingState /> : null}
      <ul className={styles.list}>
        {(jobsQ.data?.jobs ?? []).map((j) => (
          <li key={j.id} className={styles.row}>
            <span>
              {j.name} — {j.status}
              {j.config?.source === 'apify' ? ` · Apify (${j.config.apify_preset ?? '?'})` : ''}
              {j.config?.apify_last_imported != null
                ? ` · ${j.config.apify_last_imported} geïmporteerd`
                : ''}
              {j.pending_count ? ` (${j.pending_count} pending)` : ''}
              {j.last_error ? (
                <span className={styles.mutedSmall}> — {j.last_error.slice(0, 120)}</span>
              ) : null}
            </span>
            <Button
              type="button"
              variant="ghost"
              disabled={!['draft', 'completed', 'failed', 'paused'].includes(j.status)}
              onClick={() => runJob.mutate(j.id)}
            >
              Run
            </Button>
          </li>
        ))}
      </ul>
      {!jobsQ.data?.jobs?.length ? (
        <StateMessage title="Nog geen scrape jobs">
          Kies seed URLs of een Apify-segment om te starten.
        </StateMessage>
      ) : null}

      <h3>Pending kandidaten (top 100)</h3>
      <ul className={styles.list}>
        {(candidatesQ.data?.candidates ?? []).slice(0, 10).map((c) => (
          <li key={c.id} className={styles.mutedSmall}>
            {c.company ?? c.email ?? c.id} · score {c.confidence_score}
          </li>
        ))}
      </ul>
      {(candidatesQ.data?.candidates.length ?? 0) > 10 ? (
        <p className={styles.mutedSmall}>… en meer in Scraper review queue.</p>
      ) : null}

      <Button
        type="button"
        variant="secondary"
        onClick={() => syncLeads.mutate()}
        disabled={syncLeads.isPending}
      >
        Approved leads syncen naar doelgroep
      </Button>
    </section>
  );
}
